import React, { useState } from 'react';
import classnames from 'classnames';
import queryString from 'query-string';
import moment from 'moment';

import makeFullName from 'common/util/makeFullName';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import isUserAgentMobileApp from 'common/util/isUserAgentMobileApp';
import sendDataToMobileApp, { SR_MOBILE_APP_ACTION_TYPE_OPEN_URL } from 'client/helpers/sendDataToMobileApp';

import {
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED,
} from 'common/householdConstants';

import {
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
} from 'common/listingConstants';

import {
  STRIPE_CHARGE_STATUS_PENDING,
  STRIPE_CHARGE_STATUS_SUCCESS,
  STRIPE_CHARGE_STATUS_FAILED,
  CHARGE_TYPE_OFFLINE,
} from 'common/stripeConstants';

import {
  PAYMENT_ACCOUNT_BANK_ACCOUNT,
  PAYMENT_ACCOUNT_STATUS_PENDING_AUTOMATIC_VERIFICATION,
  PAYMENT_ACCOUNT_STATUS_PENDING_MANUAL_VERIFICATION,
} from 'common/paymentAccountConstants';

import {
  SR_API_ACTION_BITLY_CREATE_LINK,
  SR_API_ACTION_PLAID_CREATE_STRIPE_BANK_ACCOUNT,
  SR_API_ACTION_PLAID_LINK_TOKEN_ERROR,
} from 'api/actionTypes';

import {
  getHouseholdPets,
  calculateHouseholdDepositFees,
} from 'common/householdHelpers';

import {
  PET_OPTION_VALUE_DOG,
  PET_OPTION_VALUE_CAT,
} from 'common/petOptionItems';

import {
  getHouseholdQualification,
} from 'common/householdApplication/prequalificationHelpers';

import {
  isApplicationPastStatus,
  makeApplicationStatusList,
  findHouseholdApplicationSubmission,
  getHouseholdEarliestApplication,
} from 'common/householdApplication/applicationHelpers';

import {
  isListingFullService,
  isListingTenantScreening,
  getListingFullAddressString,
  getSecurityDepositSettings,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  formatPrice,
  formatPhoneNumber,
} from 'common/formatters';

import {
  RENTAL_APPLICATION_STEP_FORM,
  getRentalApplicationUrl,
} from 'common/householdApplication/rentalApplicationHelpers';

import {
  makeProxiedS3FilePath,
} from 'client/lib/fileUploader';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrExternalLink from 'client/components/SrExternalLink';
import SrCountdownClock from 'client/components/SrCountdownClock';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrModalV2, { MODAL_THEME_FIXED_FOOTER_BUTTON } from 'client/components/SrModalV2/SrModalV2';
import SrFlex from 'client/components/SrFlex';
import SrTextSeparator from 'client/components/SrTextSeparator/SrTextSeparator';
import SrLinkUrlButton from 'client/components/SrLinkUrlButton';
import SrBankAccountCard from 'client/components/SrBankAccountCard/SrBankAccountCard';
import OpenPlaidLink from 'client/components/OpenPlaidLink';
import SrWithPlaidLinkToken from 'client/components/SrWithPlaidLinkToken';
import ButtonDeprecated, {
  BUTTON_THEME_GREEN,
} from 'client/components/ButtonDeprecated';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_HEIGHT_TALL,
} from 'client/components/SrButton/SrButton';
import SrCountdownClockStaticRefresh from 'client/components/SrCountdownClockStaticRefresh';
import SrRenterApplicationStatusStepCard from 'client/components/SrRenterApplicationStatusStepCard';
import SrRenterApplicationStatusRoommatesListRow from 'client/components/SrRenterApplicationStatusRoommatesListRow';
import SrRenterApplicationInviteRoommateListRowSunroom from 'client/components/SrRenterApplicationInviteRoommateListRowSunroom';
import SrRenterApplicationUpdatePetsListRow from 'client/components/SrRenterApplicationUpdatePetsListRow';
import SrRenterApplicationUpdateJobsListRow from 'client/components/SrRenterApplicationUpdateJobsListRow';
import SrRenterApplicationStatusLinkButton from 'client/components/SrRenterApplicationStatusLinkButton';
import SrRenterApplicationStatusStepApproval from 'client/components/SrRenterApplicationStatusStepApproval';

import postWithState from 'client/actions/postWithState';
import { INCOME_VERIFICATION_STATUS_COMPLETED, INCOME_VERIFICATION_STATUS_FAILED } from 'common/incomeVerificationConstants';

const SR_RENTER_APPLICATION_STEP_ID_APPS_IN_PROGRESS = 'appsInProgress';
const SR_RENTER_APPLICATION_STEP_ID_PET_SCREENING = 'petScreening';
const SR_RENTER_APPLICATION_STEP_ID_VERIFICATION = 'verification';
const SR_RENTER_APPLICATION_STEP_ID_PENDING_APPROVAL = 'pendingApproval';
const SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT = 'securityDeposit';
const SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGN = 'leaseSign';
const SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED = 'leaseSigned';

const SR_RENTAL_APPLICATION_STEP_LIST = [
  SR_RENTER_APPLICATION_STEP_ID_APPS_IN_PROGRESS,
  SR_RENTER_APPLICATION_STEP_ID_PET_SCREENING,
  SR_RENTER_APPLICATION_STEP_ID_VERIFICATION,
  SR_RENTER_APPLICATION_STEP_ID_PENDING_APPROVAL,
  SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT,
  SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGN,
  SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED,
];

const applicationStatuListInVerificationStep = makeApplicationStatusList({
  fromApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_PAID,
  toApplicationStatus: HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
});

const getPetType = (petType) => {
  if (petType === PET_OPTION_VALUE_DOG) {
    return 'Doggy';
  }
  if (petType === PET_OPTION_VALUE_CAT) {
    return 'Kitty';
  }
  return 'Pet';
};

const SrRenterApplicationStatusSteps = ({
  listing,
  partner,
  moveInDate,
  householdListing,
  householdUsers,
  roommateApplications,
  securityDepositStripeCharge,
  securityDepositPaymentAccount,
  defaultPaymentAccount,
  guarantorApplications,
  applicationSubmissions,
  typeformIdRenterLeaseSigned,
  householdApplicationsStatusUpdates,
  tenantScreeningCriteria,
  tenantAlertReports,
  householdGuarantors,
  renters,
  household,
  lease,
  marketingPartnerListing,
  onInviteRoommateClicked,
  currentUser,
  dispatch,
  history,
  appUrl,
  userAgent,
  viewing,
  viewingUserId,
  market,
}) => {
  const [microDepositsPlaidAccessToken, setMicroDepositsPlaidAccessToken] = useState(null);
  const [microDepositsCompletedDate, setMicroDepositsCompletedDate] = useState(null);
  const [microDepositsFailedDate, setMicroDepositsFailedDate] = useState(null);
  const isMicroDepositState = (
    (!securityDepositStripeCharge || securityDepositStripeCharge.status === STRIPE_CHARGE_STATUS_FAILED)
    && defaultPaymentAccount
    && [
      PAYMENT_ACCOUNT_STATUS_PENDING_AUTOMATIC_VERIFICATION,
      PAYMENT_ACCOUNT_STATUS_PENDING_MANUAL_VERIFICATION,
    ].includes(defaultPaymentAccount.status)
  );

  const securityDepositPaymentLink = (
    `/security-deposit/${householdListing.householdId}/${householdListing.listingId}`
  );

  const [showInviteGuarantorModal, setShowInviteGuarantorModal] = useState(false);
  const applicationSteps = [];
  const rommateApplication = roommateApplications.find(({ userId }) => {
    return viewingUserId ? userId === viewingUserId : userId === currentUser.id;
  });
  const guarantorApplication = guarantorApplications.find(({ userId }) => {
    return viewingUserId ? userId === viewingUserId : userId === currentUser.id;
  });
  const householdApplication = rommateApplication || guarantorApplication;
  const earliestApplication = getHouseholdEarliestApplication(roommateApplications);
  const householdUsersByUserId = householdUsers.reduce((memo, householdUser) => ({
    ...memo,
    [householdUser.userId]: householdUser,
  }), {});
  const tenantAlertReportByUserId = tenantAlertReports
    ? tenantAlertReports.reduce((memo, tenantAlertReport) => ({
      ...memo,
      [tenantAlertReport.userId]: tenantAlertReport,
    }), {}) : null;
  const tenantAlertReportsGenerated = tenantAlertReports ? [
    ...roommateApplications,
    ...guarantorApplications,
  ].every(({ userId }) => tenantAlertReportByUserId[userId]) : null;

  const listingFullService = isListingFullService(listing);
  const listingTenantScreening = isListingTenantScreening(listing);

  const listingSecurityDepositSettings = getSecurityDepositSettings({ listing, partner });
  const securityDepositLabel = getMarketSecurityDepositLabel(market);

  const shouldCollectSecurityDepositAndFees = !!(
    listingSecurityDepositSettings.whoCollectsSecurityDepositPetFees !== LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL // eslint-disable-line max-len
  ) && listingFullService;

  const { pets, nonEsaPets } = getHouseholdPets({
    householdApplications: roommateApplications,
  });

  const householdQualification = tenantScreeningCriteria
    ? getHouseholdQualification({
      moveInDate: moveInDate,
      listing,
      tenantScreeningCriteria,
      renters,
      householdApplications: roommateApplications,
      householdGuarantors,
      guarantorApplications: guarantorApplications,
      tenantAlertReports,
      leadRenterId: household.leadRenterId,
      partner,
    })
    : null;

  const depositFees = calculateHouseholdDepositFees({
    listing,
    householdListing,
    pets: nonEsaPets,
  });

  const depositTotal = shouldCollectSecurityDepositAndFees && securityDepositPaymentAccount
    ? formatPrice(securityDepositPaymentAccount.type === PAYMENT_ACCOUNT_BANK_ACCOUNT
      ? depositFees.bankTotal
      : depositFees.cardTotal)
    : formatPrice(depositFees.totalWithoutStripeFees);

  const securityDepositPayingForLabel = shouldCollectSecurityDepositAndFees
    ? `${securityDepositLabel}${depositFees.petDeposit || depositFees.petFees
      ? ' and pet fees'
      : ''}`
    : securityDepositLabel;

  let currentApplicationStep = null;

  if (earliestApplication.status === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED;
  }
  else if (isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID)) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGN;
  }
  else if (earliestApplication.status === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT;
  }
  else if (earliestApplication.status === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_PENDING_APPROVAL;
  }
  else if (applicationStatuListInVerificationStep.includes(earliestApplication.status)) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_VERIFICATION;
  }
  else {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_APPS_IN_PROGRESS;
  }

  if (guarantorApplications.length) {
    const earliestGuarantorApplication = getHouseholdEarliestApplication(guarantorApplications);

    if (!isApplicationPastStatus(earliestGuarantorApplication, HOUSEHOLD_APPLICATION_STATUS_PAID)) {
      currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_APPS_IN_PROGRESS;
    }
  }

  const applicationRejected = (
    householdListing.cancelReason === HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED
    || householdListing.cancelReason === HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED_BY_PARTNER
  );
  if (applicationRejected) {
    currentApplicationStep = SR_RENTER_APPLICATION_STEP_ID_PENDING_APPROVAL;
  }

  const currentApplicationStepIndex = SR_RENTAL_APPLICATION_STEP_LIST.indexOf(currentApplicationStep);

  const appsInProgress = (
    roommateApplications.some((application) => !isApplicationPastStatus(application, HOUSEHOLD_APPLICATION_STATUS_PAID)) // eslint-disable-line max-len
    || guarantorApplications.some((application) => !isApplicationPastStatus(application, HOUSEHOLD_APPLICATION_STATUS_PAID)) // eslint-disable-line max-len
  );

  const displayHouseholdIncomeNotQualifiedWarning = (
    !appsInProgress
    && householdQualification
    && !householdQualification.householdRequirements.monthlyIncome.isQualified
  );

  const displayHouseholdCreditNotQualifiedWarning = (
    !appsInProgress
    && tenantAlertReportsGenerated
    && householdQualification
    && !householdQualification.householdRequirements.creditScore.isQualified
  );

  const generateGuarantorInviteLinkContent = (url, type, inviteGuarantorMessage) => {
    const inviteGuarantorSubject = global.encodeURIComponent(
      'Sunroom Rentals: Guarantor Invitation'
    );

    switch (type) {
      case 'copyLink':
        return url;
      case 'email':
        return `mailto:?subject=${inviteGuarantorSubject}` +
          `&body=${inviteGuarantorMessage}`;
      case 'text':
        return `sms:%20&body=${inviteGuarantorMessage}`;
      default:
        return url;
    }
  };

  const longUrl = (
    `${appUrl}/invite-guarantor?&name=${encodeURIComponent(currentUser.name)}` +
    `&userId=${currentUser.id}` +
    `&phone=${currentUser.phone}` +
    `&householdId=${household.id}` +
    `&listingId=${listing.id}` +
    `&street=${encodeURIComponent(listing.addressStreetDisplay)}`
  );

  const onGenerateGuarantorLink = (type) => {
    const isMobileApp = isUserAgentMobileApp(userAgent);

    dispatch(postWithState, {
      action: SR_API_ACTION_BITLY_CREATE_LINK,
      payload: {
        longUrl: longUrl,
      },
      onSuccess: (bitlyLink) => {
        const inviteGuarantorMessage = global.encodeURIComponent(
          '👋 Hey, I added you as my Guarantor on my rental application on Sunroom Rentals. ' +
          'Please apply using this link ' + bitlyLink || longUrl
        );

        const linkUrlContent =
          generateGuarantorInviteLinkContent(bitlyLink, type, inviteGuarantorMessage);

        if (isMobileApp) {
          sendDataToMobileApp({
            actionType: SR_MOBILE_APP_ACTION_TYPE_OPEN_URL,
            url: linkUrlContent,
          });
        }
        else {
          global.open(linkUrlContent, '_blank');
        }
      },
    });
  };

  const InviteGuarantorModal = () => (
    <SrModalV2
      onClose={() => setShowInviteGuarantorModal(false)}
      theme={MODAL_THEME_FIXED_FOOTER_BUTTON}
      buttonTheme={SR_BUTTON_THEME_ADOBE_GREY}
      buttonText='Cancel'
      contentMaxWidth='400px'
      contentOverflow='hidden'
    >
      <SrBox padding='0 14px 124px 14px'>
        <SrBox marginBottom='15px'>
          <SrText
            font='greycliff'
            color='blueGreycliff'
            fontSize='30px'
            lineHeight='36px'
            weight='800'
            display='block'
          >
            Invite Guarantor
          </SrText>
        </SrBox>
        <SrBox marginBottom='25px'>
          <SrText
            font='greycliff'
            fontSize='16px'
            lineHeight='25px'
            weight='600'
            color='blueGreycliff'
            opacity='0.5'
            display='block'
          >
            {`Guarantor must have a monthly income that is
            ${tenantScreeningCriteria.guarantorRentToIncomeRatio || '5'}x the rent or more,
            have a credit score of ${tenantScreeningCriteria.creditScore || '650'}
            or above, and have a clean background.`}
          </SrText>
        </SrBox>
        <SrFlex justify='center'>
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_BLUE}
            heightType={SR_BUTTON_HEIGHT_TALL}
            hasBoxShadow
            type='button'
            minWidth='calc((100% - 19px) / 2)'
            onClick={() => onGenerateGuarantorLink('email')}
          >
            Email
          </SrButton>
          <SrBox width='19px' />
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREEN}
            heightType={SR_BUTTON_HEIGHT_TALL}
            hasBoxShadow
            type='button'
            minWidth='calc((100% - 19px) / 2)'
            onClick={() => onGenerateGuarantorLink('text')}
          >
            Text
          </SrButton>
        </SrFlex>
        <SrTextSeparator />
        <SrBox marginTop='18px'>
          <SrLinkUrlButton
            buttonText='Copy Link'
            href={longUrl}
          />
        </SrBox>
      </SrBox>
    </SrModalV2>
  );

  const InviteGuarantorLabel = ({ marginBottom = 0 }) =>
    (tenantScreeningCriteria // eslint-disable-line no-constant-condition
      && tenantScreeningCriteria.guarantorAccepted
      && (displayHouseholdIncomeNotQualifiedWarning || displayHouseholdCreditNotQualifiedWarning))
      && !viewingUserId
      // TODO(@Sunroom): We do not ask for their monthly income just after finishing their app.
      // Fix the logic to not always show this warning message.
      && false
      ? (
        <SrBox marginTop='18px' marginBottom={marginBottom}>
          <SrLabel
            color='red'
            backgroundColor='rgba(232, 0, 29, 0.08)'
            label={(
              <>
                <SrText
                  color='red'
                  font='greycliff'
                  weight='800'
                  fontSize='18px'
                  lineHeight='26px'
                  paddingBottom='3px'
                  letterSpacing='0.5px'
                >
                  Guarantor needed to meet the&nbsp;
                  {displayHouseholdIncomeNotQualifiedWarning && 'income '}
                  {displayHouseholdIncomeNotQualifiedWarning &&
                    displayHouseholdCreditNotQualifiedWarning && '& '}
                  {displayHouseholdCreditNotQualifiedWarning && 'credit '}
                  requirement. Your application is not being processed right now.&nbsp;
                </SrText>
                <SrText
                  color='blue300'
                  font='greycliff'
                  weight='800'
                  fontSize='18px'
                  lineHeight='26px'
                  paddingBottom='3px'
                  letterSpacing='0.5px'
                  whiteSpace='nowrap'
                  onClick={() => setShowInviteGuarantorModal(true)}
                  cursor='pointer'
                >
                  Invite Guarantor
                  <SrBox marginLeft='5px' display='inlineBlock'>
                    <img alt='right arrow' src='/images/arrow-small-right-blue-300-curved.svg' />
                  </SrBox>
                </SrText>
              </>
            )}
            iconUrl='/images/caution-icon-red.svg'
            iconDropShadow='0 3px 4px rgba(232, 0, 29, 0.27)'
          />
        </SrBox>
      ) : null;

  applicationSteps.push({
    id: SR_RENTER_APPLICATION_STEP_ID_APPS_IN_PROGRESS,
    title: 'applicants',
    inProgress: appsInProgress,
    body: () => {
      return (
        <>
          <div className={classnames('SrRenterApplicationStatusDescription', 'descriptionText')}>
            All adults aged 18 or older must complete their application forms and pay
            a {formatPrice(listing.applicationFee)} application fee.
            See the progress of everyone in your household below.
          </div>
          <InviteGuarantorLabel marginBottom='-7px' />
          <div className='SrRenterApplicationStatusSubheader'>
            your household
          </div>
          <div className='SrRenterApplicationStatusRoommatesList'>
            {roommateApplications.map((app) => (
              <SrRenterApplicationStatusRoommatesListRow
                key={`srasrlr-${app.id}`}
                application={app}
                dispatch={dispatch}
                history={history}
                currentUser={currentUser}
                listing={listing}
                disabled={applicationRejected || viewingUserId}
              />
            ))}
            {guarantorApplications.map((app) => (
              <SrRenterApplicationStatusRoommatesListRow
                key={`srasrlr-grtor-${app.id}`}
                application={app}
                applicationSubmission={findHouseholdApplicationSubmission({
                  applicationSubmissions,
                  applicationId: app.id,
                  listingId: listing.id,
                })}
                dispatch={dispatch}
                history={history}
                currentUser={currentUser}
                listing={listing}
                disabled={applicationRejected || viewingUserId}
                isGuarantor
              />
            ))}
          </div>
          {!isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD)
            && !viewingUserId
            && !applicationRejected
            && onInviteRoommateClicked && (
            <SrRenterApplicationInviteRoommateListRowSunroom
              listing={listing}
              householdId={householdListing.householdId}
              onInviteRoommateClicked={onInviteRoommateClicked}
            />
          )}
        </>
      );
    },
  });

  if (partner?.petScreeningPropertyManagerId && pets.length) {
    const applicationPastPendingLandlordStatus = isApplicationPastStatus(
      earliestApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD
    );
    const petAppInProgress = (
      pets.some((pet) => !pet.petScreeningProfileId)
      && !applicationPastPendingLandlordStatus
    );

    applicationSteps.push({
      id: SR_RENTER_APPLICATION_STEP_ID_PET_SCREENING,
      title: 'pets',
      inProgress: petAppInProgress,
      body: ({ inProgress }) => {
        const rentalApplicationUrl = getRentalApplicationUrl({
          userId: viewingUserId || currentUser.id,
          listingId: listing.id,
          rentalApplicationStepId: RENTAL_APPLICATION_STEP_FORM,
          queryObject: {
            goToFormFieldId: 'petCount',
          },
        });

        const ownPetNames = (householdApplication.pets || [])
          .map((pet) => pet.name.toLowerCase());

        return (
          <>
            <div className='SrRenterApplicationStatusDescription'>
              Pet screening is required for each applicant's pets and service animals.
              There is a $25 or $20 per pet fee depending on the payment method.
              Complete the "Assistance Animals" pet screening form for your emotional
              support or service animals (ESA).
              There is no charge for ESA screening.
              Sunroom does not make money from this fee, it all goes to the screening company.
            </div>
            <InviteGuarantorLabel marginBottom='-7px' />
            <div className='SrRenterApplicationStatusSubheader'>
              everyone's pets (updated hourly)
            </div>
            <div className='SrRenterApplicationStatusPetsList'>
              <div>
                {pets.map((pet) => {
                  return (
                    <div key={pet.name} className='SrRenterApplicationStatusPetsListRow'>
                      <div className='petImageContainer'>
                        <div
                          className='petSnippet'
                          style={{ backgroundImage: `url(${makeProxiedS3FilePath(pet.photo)})` }}
                        />
                      </div>
                      <div className='petInfoContainer'>
                        <div className='nameAndStatusContainer'>
                          <div className='textContainer'>
                            <span className='name'>
                              {pet.name}
                            </span>
                            <span className='status completed'>
                              Cute {getPetType(pet.type)}
                            </span>
                            <span className={classnames('status', {
                              incomplete: !pet.petScreeningProfileId && !applicationPastPendingLandlordStatus,
                              completed: !!pet.petScreeningProfileId || applicationPastPendingLandlordStatus,
                            })}
                            >
                              Form Submitted
                            </span>
                          </div>
                        </div>
                        <div className='rightButtonContainer'>
                          {ownPetNames.includes(pet.name.toLowerCase())
                            && !viewingUserId
                            && (
                              <SrExternalLink
                                className={classnames('petLink', petAppInProgress ? null : 'disabled')}
                                link='https://app.petscreening.com/referral/0IShf3YZvZsh'
                              >
                                start
                              </SrExternalLink>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {inProgress && (
                <SrRenterApplicationUpdatePetsListRow
                  to={rentalApplicationUrl}
                />
              )}
            </div>
          </>
        );
      },
    });
  }

  const applicationStepIdVerificationIndex = SR_RENTAL_APPLICATION_STEP_LIST
    .indexOf(SR_RENTER_APPLICATION_STEP_ID_VERIFICATION);
  applicationSteps.push({
    id: SR_RENTER_APPLICATION_STEP_ID_VERIFICATION,
    title: 'job & rent verified',
    body: ({ inProgress, completed }) => {
      const rentalApplicationUrl = getRentalApplicationUrl({
        userId: viewingUserId || currentUser.id,
        listingId: listing.id,
        rentalApplicationStepId: RENTAL_APPLICATION_STEP_FORM,
        queryObject: {
          goToFormFieldId: 'currentJobCompany',
        },
      });

      return (
        <>
          <SrBox marginTop='12px'>
            <SrText
              font='whitney'
              fontSize='16px'
              lineHeight='25px'
              color='blue'
              weight='500'
            >
              If Sunroom doesn’t get a response from your employer &amp; landlord in 24 hours,
              then we will move to the next applicant.&nbsp;&nbsp;
              <SrText
                weight='400'
              >
                Give them a heads up that Sunroom will reach out soon!
              </SrText>
            </SrText>
          </SrBox>
          <InviteGuarantorLabel marginBottom='-7px' />
          <div className='SrRenterApplicationStatusSubheader'>
            verifications
          </div>
          <div className='SrRenterApplicationStatusRoommatesList'>
            <div>
              {roommateApplications.map((roommateApplication) => {
                const householdUser = householdUsersByUserId[roommateApplication.userId];
                const isIncomeVerification = (
                  partner.acceptBankDataAsVoe
                  && roommateApplication.incomeVerificationId
                  && roommateApplication.incomeVerificationStatus !== INCOME_VERIFICATION_STATUS_FAILED
                );
                const jobVerificationCompleted = (
                  (isIncomeVerification
                    && roommateApplication.incomeVerificationStatus === INCOME_VERIFICATION_STATUS_COMPLETED
                  )
                  || !!householdUser.typeFormEmployementVerificationToken
                  || householdApplication.employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED // eslint-disable-line max-len
                  || currentApplicationStepIndex > applicationStepIdVerificationIndex
                );
                const rentalVerificationCompleted = (
                  !!householdUser.typeFormRentalVerificationToken
                  || currentApplicationStepIndex > applicationStepIdVerificationIndex
                );

                return (
                  <div key={roommateApplication.id} className='SrRenterApplicationVoeVorListRow'>
                    <div className='roommateImageContainer'>
                      <div className='roommateSnippet'>
                        {roommateApplication.firstName.substring(0, 1).toUpperCase()}
                      </div>
                    </div>
                    <div className='roommateInfoContainer'>
                      <div className='nameAndStatusContainer'>
                        <div className='textContainer'>
                          <span className='name'>
                            {makeFullName(roommateApplication.firstName, roommateApplication.lastName)}
                          </span>
                          <span className={classnames('status', {
                            completed: jobVerificationCompleted,
                            incomplete: !jobVerificationCompleted,
                          })}
                          >
                            {householdApplication.employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED // eslint-disable-line max-len
                              ? 'Not currently employed'
                              : `Job Verified ${isIncomeVerification
                                ? 'by your bank'
                                : roommateApplication.currentJobSupervisorName
                                  ? ` by ${roommateApplication.currentJobSupervisorName}`
                                  : ''}`
                            }
                          </span>
                          {!jobVerificationCompleted &&
                            (roommateApplication.currentJobEmployerEmail
                              || roommateApplication.currentJobSupervisorPhoneNumber) &&
                              <SrBox marginLeft='13px' marginBottom='3px'>
                                <SrText
                                  display='block'
                                  fontSize={12}
                                  lineHeight='15px'
                                >
                                  {roommateApplication.currentJobEmployerEmail}
                                </SrText>
                                <SrText
                                  display='block'
                                  fontSize={12}
                                  lineHeight='15px'
                                >
                                  {formatPhoneNumber(roommateApplication.currentJobSupervisorPhoneNumber)}
                                </SrText>
                              </SrBox>
                          }
                          <span className={classnames('status', {
                            completed: rentalVerificationCompleted,
                            incomplete: !rentalVerificationCompleted,
                          })}
                          >
                            Rental Verified{roommateApplication.currentLandlordName ?
                              ` by ${roommateApplication.currentLandlordName}` : ''}
                          </span>
                          {!rentalVerificationCompleted &&
                            (roommateApplication.currentLandlordEmail
                              || roommateApplication.currentLandlordPhone) &&
                              <SrBox marginLeft='13px' marginBottom='3px'>
                                <SrText
                                  display='block'
                                  fontSize={12}
                                  lineHeight='15px'
                                >
                                  {roommateApplication.currentLandlordEmail}
                                </SrText>
                                <SrText
                                  display='block'
                                  fontSize={12}
                                  lineHeight='15px'
                                >
                                  {formatPhoneNumber(roommateApplication.currentLandlordPhone)}
                                </SrText>
                              </SrBox>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {inProgress && !completed && !viewingUserId && (
            <SrRenterApplicationUpdateJobsListRow
              to={rentalApplicationUrl}
            />
          )}
        </>
      );
    },
  });

  applicationSteps.push({
    id: SR_RENTER_APPLICATION_STEP_ID_PENDING_APPROVAL,
    title: (
      !isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD)
      || earliestApplication.status === HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD
    ) && !applicationRejected
      ? (listingTenantScreening ? 'approved or denied' : 'landlord approval')
      : (isApplicationPastStatus(earliestApplication, HOUSEHOLD_APPLICATION_STATUS_ACCEPTED)
        && !applicationRejected
        ? 'approved'
        : 'denied'),
    rejected: applicationRejected,
    body: () => {
      return (
        <SrRenterApplicationStatusStepApproval
          listing={listing}
          lease={lease}
          moveInDate={moveInDate}
          householdListing={householdListing}
          householdApplication={householdApplication}
          earliestApplication={earliestApplication}
          applicationRejected={applicationRejected}
          householdApplicationsStatusUpdates={householdApplicationsStatusUpdates}
          listingTenantScreening={listingTenantScreening}
          listingFullService={listingFullService}
          listingSecurityDepositSettings={listingSecurityDepositSettings}
          marketingPartnerListing={marketingPartnerListing}
          market={market}
          shouldCollectSecurityDepositAndFees={shouldCollectSecurityDepositAndFees}
          securityDepositPayingForLabel={securityDepositPayingForLabel}
          InviteGuarantorLabel={InviteGuarantorLabel}
          SrCountdownClockStaticRefresh={SrCountdownClockStaticRefresh}
        />
      );
    },
  });

  if (listingFullService) {
    applicationSteps.push({
      id: SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT,
      title: listing.collectFirstMonth
        ? `${securityDepositLabel} & rent`
        : securityDepositLabel,
      completed: (
        securityDepositStripeCharge?.status === STRIPE_CHARGE_STATUS_SUCCESS
      ),

      body: ({ inProgress, completed }) => {
        const householdApplicationStatusAccepted = householdApplicationsStatusUpdates
          .find(({ toStatus }) => toStatus === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED);

        const {
          securityDepositCollectionInstructions,
        } = listingSecurityDepositSettings;

        const {
          securityDepositIncreaseReason,
          securityDepositIncreaseMultiplier,
        } = householdListing;

        const isSecurityDepositIncreased = securityDepositIncreaseMultiplier > 1;

        const securityDepositText = () => {
          const depositPaid = SR_RENTAL_APPLICATION_STEP_LIST.indexOf(SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT) < currentApplicationStepIndex; // eslint-disable-line max-len
          if (depositPaid
            && securityDepositStripeCharge
            && securityDepositStripeCharge?.type === CHARGE_TYPE_OFFLINE
          ) {
            return (
              <SrBox marginTop='-5px'>
                <SrText
                  display='block'
                  font='greycliff'
                  color='green'
                  size='16'
                  letterSpacing='0.5px'
                  weight='700'
                  lineHeight='25px'
                >
                  Payment of {depositTotal} Completed
                </SrText>
                <SrBox marginTop='4px' />
                <SrText
                  display='block'
                  font='greycliff'
                  color='blueGreycliff'
                  size='14'
                  letterSpacing='0.5px'
                  weight='500'
                  lineHeight='20px'
                  opacity='0.6'
                >
                  Confirmed by {partner.displayName}
                </SrText>
                <SrText
                  display='block'
                  font='greycliff'
                  color='blueGreycliff'
                  size='14'
                  letterSpacing='0.5px'
                  weight='500'
                  lineHeight='18px'
                  opacity='0.6'
                >
                  Completed: {
                    moment(securityDepositStripeCharge.createdDate).format('MMMM Do YYYY [at] h:mm a')
                  }
                </SrText>
              </SrBox>
            );
          }

          if (
            depositPaid
            && (
              !shouldCollectSecurityDepositAndFees
              || !securityDepositPaymentAccount
            )
          ) {
            return `Thank you! Your ${securityDepositLabel} has been received by the landlord.`;
          }

          const landlordPaymentInstructions = securityDepositCollectionInstructions && (
            <>
              <SrText weight='800'>
                Landlord's instructions to pay {securityDepositLabel} & fees:
              </SrText>
              <br />
              <SrText whiteSpace='prewrap' weight='500'>
                "{securityDepositCollectionInstructions}"
              </SrText>
            </>
          );

          const landlordIncreasedReason = (
            <>
              Your application will be approved under the condition that you pay
              <SrText weight='800'>
                {` ${securityDepositIncreaseMultiplier}x ${securityDepositLabel}`}
              </SrText>.
              <br /><br />
              <SrText weight='800'>
                The landlord's reason for this:
                <br />
                <span style={{ whiteSpace: 'prewrap' }}>
                  {securityDepositIncreaseReason}
                </span>
              </SrText>
              <br /><br />
              If you want to accept these conditions and rent this home, please pay the
              {securityDepositLabel} and other fees
              {securityDepositCollectionInstructions ? ' by following the landlord\'s instructions ' : ' '}
              bellow.
              <br /><br />
              Please remember, this rental is only reserved for you once you pay the {securityDepositLabel}.
            </>
          );

          if (
            shouldCollectSecurityDepositAndFees
            && securityDepositStripeCharge?.status === STRIPE_CHARGE_STATUS_PENDING
          ) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='yellowGold'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  {`Transfer of ${depositTotal} Initiated`}
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(securityDepositStripeCharge.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    Estimated delivery: 3-7 biz days
                  </SrText>
                </SrBox>
                <SrBankAccountCard
                  bankAccount={securityDepositPaymentAccount}
                />
                <SrBox paddingTop='15px'>
                  <SrLabel
                    color='blueGreycliff'
                    backgroundColor='rgba(219, 176, 0, 0.08)'
                    iconUrl='/images/caution-icon-yellow.svg'
                    iconDropShadow='0 3px 4px rgba(219, 176, 0, 0.27)'
                    fontSize='normal'
                    label={
                      'To avoid losing the opportunity to secure this rental, please keep the amount of ' +
                      `${depositTotal} in your bank account for at least the next 7 business days.`
                    }
                  />
                </SrBox>
                <div className='SrRenterApplicationStatusSubheader'>
                  what’s next?
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Once the payment has been received we’ll send you the lease to sign!
                </div>
              </>
            );
          }

          if (
            shouldCollectSecurityDepositAndFees
            && securityDepositStripeCharge?.status === STRIPE_CHARGE_STATUS_FAILED
            && !isMicroDepositState
          ) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='red'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  {`Transfer of ${formatPrice(securityDepositStripeCharge.amount)} Failed`}
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(securityDepositStripeCharge.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                  {securityDepositStripeCharge.updatedDate && (
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='500'
                      size='14'
                      lineHeight='20px'
                      opacity='0.6'
                      display='block'
                    >
                      {`Failed ${
                        momentWithAustinTimezone(securityDepositStripeCharge.updatedDate)
                          .format('MMMM Do [at] h:mma')}`
                      }
                    </SrText>
                  )}
                </SrBox>
                <SrBankAccountCard
                  bankAccount={securityDepositPaymentAccount}
                  onChangeClick={() => {
                    history.push(securityDepositPaymentLink);
                  }}
                />
                {securityDepositStripeCharge.failedReason && (
                  <SrBox paddingTop='15px'>
                    <SrLabel
                      color='red'
                      backgroundColor='rgba(232, 0, 29, 0.08)'
                      iconUrl='/images/caution-icon-red.svg'
                      iconDropShadow='0 3px 4px rgba(232, 0, 29, 0.27)'
                      fontSize='normal'
                      label={
                        'Reason for failure: ' +
                        `${securityDepositStripeCharge.failedReason}` +
                        (!securityDepositStripeCharge.updatedDate
                          ? ''
                          : (
                            ` (${momentWithAustinTimezone(securityDepositStripeCharge.updatedDate) // eslint-disable-line max-len
                              .format('MMMM Do [at] h:mma')})`
                          )
                        )
                      }
                    />
                  </SrBox>
                )}
                <div className='SrRenterApplicationStatusSubheader'>
                  try again
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  We’ve notified the landlord of the failed payment. Please initiate another
                  payment as soon as possible in order to not delay the process any further.
                </div>
              </>
            );
          }

          if (shouldCollectSecurityDepositAndFees && microDepositsFailedDate) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='red'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  Micro-Deposits Failed
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(defaultPaymentAccount.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Failed ${
                      momentWithAustinTimezone(microDepositsFailedDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                </SrBox>
                <SrBankAccountCard
                  bankAccount={defaultPaymentAccount}
                  onChangeClick={() => {
                    history.push(securityDepositPaymentLink);
                  }}
                />
                <SrBox paddingTop='15px'>
                  <SrLabel
                    color='red'
                    backgroundColor='rgba(232, 0, 29, 0.08)'
                    iconUrl='/images/caution-icon-red.svg'
                    iconDropShadow='0 3px 4px rgba(232, 0, 29, 0.27)'
                    fontSize='normal'
                    label='Reason for failure: Failed to verify the micro-deposits amount.'
                  />
                </SrBox>
                <div className='SrRenterApplicationStatusSubheader'>
                  try again
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Please initiate another payment as soon as possible in
                  order to not delay the process any further.
                </div>
              </>
            );
          }

          if (
            shouldCollectSecurityDepositAndFees
            && securityDepositStripeCharge?.status === STRIPE_CHARGE_STATUS_SUCCESS
          ) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='green'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  {`Transfer of ${depositTotal} Completed`}
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(securityDepositStripeCharge.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                  {securityDepositStripeCharge.updatedDate && (
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='500'
                      size='14'
                      lineHeight='20px'
                      opacity='0.6'
                      display='block'
                    >
                      {`Completed ${
                        momentWithAustinTimezone(securityDepositStripeCharge.updatedDate)
                          .format('MMMM Do [at] h:mma')}`
                      }
                    </SrText>
                  )}
                </SrBox>
                <SrBankAccountCard
                  bankAccount={securityDepositPaymentAccount}
                />
                <div className='SrRenterApplicationStatusSubheader'>
                  what’s next?
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Once the payment has been received we’ll send you the lease to sign!
                </div>
              </>
            );
          }

          if (shouldCollectSecurityDepositAndFees && microDepositsCompletedDate) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='green'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  Micro-Deposits Completed
                </SrText>
                <SrBox marginTop='5px' marginBottom='5px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(defaultPaymentAccount.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                </SrBox>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Completed ${
                      momentWithAustinTimezone(microDepositsCompletedDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                </SrBox>
                <SrBankAccountCard
                  bankAccount={defaultPaymentAccount}
                />
                <div className='SrRenterApplicationStatusDescription'>
                  Pay the {securityDepositLabel} and 1st month’s rent below.
                </div>
                <div className='SrRenterApplicationStatusSubheader'>
                  what’s next?
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Once the payment has been received we’ll send you the lease to sign!
                </div>
              </>
            );
          }

          if (
            shouldCollectSecurityDepositAndFees
            && isMicroDepositState
            && defaultPaymentAccount.status === PAYMENT_ACCOUNT_STATUS_PENDING_MANUAL_VERIFICATION
          ) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='yellowGold'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  Micro-Deposits Initiated
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(defaultPaymentAccount.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                </SrBox>
                <SrBankAccountCard
                  bankAccount={defaultPaymentAccount}
                  onChangeClick={() => {
                    history.push(securityDepositPaymentLink);
                  }}
                />
                <div className='SrRenterApplicationStatusDescription'>
                  Deposits typcially take 1-2 biz days to show on your bank statement. Once you see
                  them, please finish verifying your bank account by clicking the button below.
                </div>
                <SrBox paddingTop='18px'>
                  <ButtonDeprecated
                    className='optionToDisable'
                    onClick={() => {
                      setMicroDepositsPlaidAccessToken(defaultPaymentAccount.plaidAccessToken);
                    }}
                    theme={BUTTON_THEME_GREEN}
                  >
                    confirm micro deposits
                  </ButtonDeprecated>
                </SrBox>
                <div className='SrRenterApplicationStatusSubheader'>
                  what’s next?
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Once the payment has been received we’ll send you the lease to sign!
                </div>
              </>
            );
          }

          if (
            shouldCollectSecurityDepositAndFees
            && isMicroDepositState
            && defaultPaymentAccount.status === PAYMENT_ACCOUNT_STATUS_PENDING_AUTOMATIC_VERIFICATION
          ) {
            return (
              <>
                <SrText
                  font='greycliff'
                  color='yellowGold'
                  weight='700'
                  size='16'
                  lineHeight='25px'
                  display='block'
                >
                  Micro-Deposits Initiated
                </SrText>
                <SrBox marginTop='5px' marginBottom='20px'>
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                    opacity='0.6'
                    display='block'
                  >
                    {`Initiated ${
                      momentWithAustinTimezone(defaultPaymentAccount.createdDate)
                        .format('MMMM Do [at] h:mma')}`
                    }
                  </SrText>
                </SrBox>
                <SrBankAccountCard
                  bankAccount={defaultPaymentAccount}
                  onChangeClick={() => {
                    history.push(securityDepositPaymentLink);
                  }}
                />
                <div className='SrRenterApplicationStatusDescription'>
                  We’re automatically verifying your account information.
                  Verification typically take 1-2 biz days to complete.
                </div>
                <div className='SrRenterApplicationStatusSubheader'>
                  what’s next?
                </div>
                <div className='SrRenterApplicationStatusDescription'>
                  Once the payment has been received we’ll send you the lease to sign!
                </div>
              </>
            );
          }

          if (shouldCollectSecurityDepositAndFees && listing.collectFirstMonth) {
            return (
              <>
                Congrats on your approval! Now pay the {securityDepositLabel} and 1st month’s rent below.
                <br /><br />
                Remember, this rental is only reserved for you once you pay the
                {securityDepositLabel} and 1st month’s rent.
              </>
            );
          }

          if (isSecurityDepositIncreased) {
            if (shouldCollectSecurityDepositAndFees) {
              return landlordIncreasedReason;
            }

            return (
              <>
                {landlordIncreasedReason}
                <br /><br />
                {landlordPaymentInstructions}
              </>
            );
          }

          if (shouldCollectSecurityDepositAndFees) {
            return (
              <>
                Please pay the {securityDepositLabel} and other fees below.
                <br /><br />
                Remember, this rental is only reserved for you once you pay the {securityDepositLabel}.
              </>
            );
          }

          return (
            <>
              Follow the instructions below to pay the {securityDepositPayingForLabel} of
              <SrText weight='800'> {depositTotal}</SrText> within 24 hours.
              <br /><br />
              {landlordPaymentInstructions}
            </>
          );
        };

        return (
          <>
            <div className='SrRenterApplicationStatusDescription'>
              {securityDepositText()}
            </div>
            <InviteGuarantorLabel />
            {shouldCollectSecurityDepositAndFees
              && (!isMicroDepositState || microDepositsCompletedDate)
              && (
                !securityDepositStripeCharge
                || securityDepositStripeCharge.status === STRIPE_CHARGE_STATUS_FAILED
              )
              && (
                <>
                  <SrBox marginTop='30px' marginBottom='15px'>
                    {inProgress
                      && securityDepositStripeCharge?.status !== STRIPE_CHARGE_STATUS_FAILED
                      && !microDepositsCompletedDate
                      && (
                        <SrCountdownClock
                          endDate={householdApplicationStatusAccepted && inProgress
                            ? (
                              moment(householdApplicationStatusAccepted.createdDate)
                                .add(24, 'hours')
                                .toISOString()
                            )
                            : null
                          }
                          text='Time left to pay'
                          className='optionToDisable'
                        />
                      )
                    }
                  </SrBox>
                  <ButtonDeprecated
                    className='optionToDisable'
                    disabled={
                      currentApplicationStepIndex < SR_RENTAL_APPLICATION_STEP_LIST.indexOf(
                        SR_RENTER_APPLICATION_STEP_ID_SECURITY_DEPOSIT
                      )
                      || !inProgress
                      || viewingUserId
                    }
                    href={securityDepositPaymentLink}
                    theme={BUTTON_THEME_GREEN}
                  >
                    {completed
                      ? `${depositTotal} paid!`
                      : 'review & pay'
                    }
                  </ButtonDeprecated>
                  <div className='surveyNote'>
                    <span style={{ marginRight: '4px' }} role='img' aria-label='wave'>&#128274;</span>
                    Secure payment with SSL encryption
                  </div>
                </>
              )}
          </>
        );
      },
    });
  }

  if (listingFullService) {
    applicationSteps.push({
      id: SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGN,
      title: 'lease sign',
      body: () => (
        <>
          <div className='SrRenterApplicationStatusDescription'>
            You will soon receive instructions from Sunroom on how to sign the lease.
            Each applicant must sign. The landlord will sign afterwards, making the lease official.
          </div>
          <InviteGuarantorLabel marginBottom='-7px' />
          <div className='SrRenterApplicationStatusSubheader'>
            what’s next?
          </div>
          <div className='SrRenterApplicationStatusDescription'>
            After you sign the lease, you will recieve instructions on how to move in, pay rent, submit
            maintenance requests, and upload utility and insurance documents. All questions about the
            property should then be sent to the landlord/property manger.
          </div>
        </>
      ),
    });
  }

  const currentHouseholdUser = householdUsersByUserId[currentUser.id];
  const typeformSurveyUrl = typeformIdRenterLeaseSigned
    ? (
      `https://sunroom.typeform.com/to/${typeformIdRenterLeaseSigned}` +
      `#${queryString.stringify({
        householduserid: currentHouseholdUser?.id,
        renteruserid: householdApplication.userId,
        renterphone: householdApplication.phone,
        renterfirstname: householdApplication.firstName,
        partnername: partner ? partner.displayName : null,
        listingaddress: getListingFullAddressString(listing),
      })}`
    )
    : '--Missing TYPEFORM_ID_RENTER_LEASE_SIGNED env value--';

  if (listingFullService) {
    applicationSteps.push({
      id: SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED,
      title: 'move in!',
      completed: momentWithAustinTimezone()
        .isSameOrAfter(lease?.startDate || householdApplication.moveInDate, 'day'),
      body: () => {
        return (
          <>
            <div className='approvedImageContainer'>
              <img className='approvedImage' alt='approved' src='/images/skateboard-go.svg' />
            </div>
            <h3 className='approvedHeader'>get movin'</h3>
            <div className={classnames('SrRenterApplicationStatusDescription', 'centerText')}>
              Congratulations on your new home!!
              Your move-in date is
              {momentWithAustinTimezone(lease?.startDate || moveInDate).format('M/D/YYYY')}
              and the lease is for {lease?.term || householdApplication.leaseTerm} months
              with a {formatPrice(lease?.rentPrice || householdListing?.rentPrice || listing.price)} rent.
            </div>
            <div className={classnames('SrRenterApplicationStatusDescription', 'centerText')}>
              Our hard-working team in Austin, TX would love to get your feedback on how we did!
              Our survey just takes 2 minutes to do.
            </div>
            <InviteGuarantorLabel />
            <SrRenterApplicationStatusLinkButton
              to={typeformSurveyUrl}
              target='blank'
              className='optionToDisable'
              disabled={
                currentApplicationStepIndex < SR_RENTAL_APPLICATION_STEP_LIST.indexOf(
                  SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED
                )
                || !!currentHouseholdUser?.typeFormMoveInToken
                || viewingUserId
              }
            >
              take 2 min survey
            </SrRenterApplicationStatusLinkButton>
            <div className='surveyNote'>
              <span style={{ marginRight: '4px' }} role='img' aria-label='wave'>&#128075;&nbsp;</span>
              Help us improve our app for other renters
            </div>
          </>
        );
      },
    });
  }

  const stepsCompleted = applicationSteps.filter((applicationStep) => {
    const applicationStepIndex = SR_RENTAL_APPLICATION_STEP_LIST.indexOf(applicationStep.id);
    return (
      applicationStep.completed || currentApplicationStepIndex > applicationStepIndex
    ) && !applicationStep.inProgress;
  }).length;

  return (
    <>
      <div className='stepsContainer'>
        <span className='header'>
          application steps
          <span className='completedCount'>({stepsCompleted}/{applicationSteps.length} completed)</span>
        </span>
      </div>
      {applicationSteps.map((applicationStep, i) => {
        const applicationStepIndex = SR_RENTAL_APPLICATION_STEP_LIST.indexOf(applicationStep.id);
        const applicationStepCompleted = (
          applicationStep.completed
          || currentApplicationStepIndex > applicationStepIndex
        );
        const applicationStepInProgress = (
          applicationStep.inProgress
          || currentApplicationStepIndex === applicationStepIndex
        );
        const applicationStepDisabled = currentApplicationStepIndex < applicationStepIndex;
        return (
          <SrRenterApplicationStatusStepCard
            key={applicationStep.id}
            cardId={`SrRenterApplicationStatusStepCard:${householdApplication.id}:${applicationStep.id}`}
            title={applicationStep.title}
            lastSection={applicationSteps.length === (i + 1)}
            body={applicationStep.body}
            disabled={applicationStepDisabled}
            inProgress={applicationStepInProgress && (
              !applicationStepCompleted
              || applicationStep.id !== SR_RENTER_APPLICATION_STEP_ID_LEASE_SIGNED
            )}
            completed={applicationStepCompleted}
            rejected={applicationStep.rejected}
            defaultExpandedValue={currentApplicationStep === applicationStep.id && viewing}
            previouslyVisited
          />
        );
      })}

      {microDepositsPlaidAccessToken &&
        <SrWithPlaidLinkToken
          accessToken={microDepositsPlaidAccessToken}
          userId={currentUser.id}
        >
          {({ plaidLinkToken }) => (
            <OpenPlaidLink
              linkToken={plaidLinkToken}
              onSuccess={(public_token, metadata) => {
                dispatch(postWithState, {
                  action: SR_API_ACTION_PLAID_CREATE_STRIPE_BANK_ACCOUNT,
                  payload: {
                    publicToken: public_token,
                    metadata,
                    listingIdForNotification: listing.id,
                  },
                  onSuccess: () => {
                    setMicroDepositsPlaidAccessToken(null);
                    setMicroDepositsCompletedDate(new Date().toISOString());
                  },
                  onError: () => {
                    setMicroDepositsPlaidAccessToken(null);
                  },
                });
              }}
              onExit={(error, metadata) => {
                if (error) {
                  setMicroDepositsFailedDate(new Date().toISOString());
                  dispatch(postWithState, {
                    action: SR_API_ACTION_PLAID_LINK_TOKEN_ERROR,
                    payload: {
                      error,
                      metadata,
                      paymentAccountId: defaultPaymentAccount?.id,
                    },
                    onSuccess: () => {
                      setMicroDepositsPlaidAccessToken(null);
                    },
                    onError: () => {
                      setMicroDepositsPlaidAccessToken(null);
                    },
                  });
                }
              }}
            />
          )}
        </SrWithPlaidLinkToken>
      }
      {showInviteGuarantorModal && <InviteGuarantorModal />}
    </>
  );
};

SrRenterApplicationStatusSteps.displayName = 'SrRenterApplicationStatusSteps';

export default SrRenterApplicationStatusSteps;
